// components/TextSection.tsx
import React from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { TextBadge } from '@/UIlibrary/TextBadge/TextBadge';
import Checkbox from '@/UIlibrary/Checkbox/Checkbox';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { RegistryStatus } from '@/graphql/__generated__/onboarding';

interface TextSectionProps {
	label: string;
	value: any;
	field: string;
	sectionName: string;
	registryStatus?: RegistryStatus | null;
	onChangeRequest?: (
		e: React.ChangeEvent<HTMLInputElement>,
		section: string,
		field: string,
		value: any
	) => void;
	changesRequested?: Array<{ section: string; field: string; value: any }>;
	cantRequestChangesFromFields?: string[];
}

export const TextSection: React.FC<TextSectionProps> = ({
	label,
	value,
	field,
	sectionName,
	registryStatus,
	onChangeRequest,
	changesRequested = [],
	cantRequestChangesFromFields = []
}) => {
	const t = useTranslations('RegistryDetails');
	const mandatoryFields = [
		'FirstLastName',
		'Names',
		'State',
		'City',
		'PostalCode',
		'CURP',
		'MaritalStatus',
		'Rfc',
		'DateOfBirth'
	];

	const displayValue = () => {
		const optionalValues = [
			'ClabeBankName',
			'TaxRegimeCode',
			'CountryOfBirth',
			'StateOfBirth',
			'SecondLastName',
			'InternalNumber',
			'BetweenStreetsOne',
			'BetweenStreetsTwo'
		];

		if (optionalValues.includes(field) && value === '') {
			return <TextBadge text={t('Optional')} size="sm" type="gray" />;
		} else if (value === null || value === undefined || value === '') {
			return <TextBadge text={t('Pending')} size="sm" type="gray" />;
		}

		if (typeof value === 'boolean') {
			return (
				<Typography size="md" weight="bold">
					{value ? t('Yes') : t('No')}
				</Typography>
			);
		}
		return (
			<Typography size="md" weight="bold">
				{value}
			</Typography>
		);
	};

	const showChangeRequest =
		onChangeRequest &&
		(registryStatus === RegistryStatus.COMPLETED || registryStatus === RegistryStatus.REJECTED);

	return (
		<div className="flex items-center gap-2">
			{showChangeRequest ? (
				<Checkbox
					name={field}
					checked={changesRequested.some(
						(change) => change.field === field && change.value === value
					)}
					onChange={(e) =>
						onChangeRequest!(
							e,
							sectionName.charAt(0).toLowerCase() + sectionName.slice(1),
							field,
							value
						)
					}
					disabled={cantRequestChangesFromFields.includes(field)}
				/>
			) : (
				onChangeRequest && (
					<Tooltip content={t('ChangeRequestDisabled')}>
						<Checkbox name={field} checked={false} onChange={() => {}} disabled={true} />
					</Tooltip>
				)
			)}
			<div className="flex justify-between items-center w-full">
				<Typography size="sm" color={GRAY_COLORS.gray600}>
					{mandatoryFields.includes(label)
						? t(toTranslationKey(label)) + '*'
						: t(toTranslationKey(label))}
					:
				</Typography>
				{displayValue()}
			</div>
		</div>
	);
};

export default TextSection;
